import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Building2, Search } from "lucide-react";
import { toast } from "sonner";

export const ProductKlantcodes = ({ productId }) => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // org_id -> {klantcode, verkoopprijs}
  // nieuwe rij
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [newPrijs, setNewPrijs] = useState("");
  const tmr = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/producten/${productId}/klantcodes`);
    setRows(data);
    setDrafts({});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  useEffect(() => {
    if (picked && q === picked.naam) return;
    clearTimeout(tmr.current);
    if (!q || q.length < 2) { setResults([]); return; }
    tmr.current = setTimeout(async () => {
      const { data } = await api.get("/organisaties", { params: { q } });
      setResults((data || []).slice(0, 6));
    }, 250);
    return () => clearTimeout(tmr.current);
  }, [q, picked]);

  const saveRow = async (orgId, body, melding) => {
    try {
      await api.put(`/organisaties/${orgId}/prijzen/${productId}`, body);
      toast.success(melding);
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const onBlurField = (r, field, value) => {
    const huidig = field === "klantcode" ? (r.klantcode || "") : (r.verkoopprijs ?? "");
    if (field === "klantcode") {
      if (value.trim() === huidig) return;
      saveRow(r.organisatie_id, { klantcode: value.trim() }, value.trim() ? "Klantcode opgeslagen" : "Klantcode gewist");
    } else {
      if (value === "" ) return;
      const num = parseFloat(String(value).replace(",", "."));
      if (isNaN(num) || num < 0) { toast.error("Ongeldige prijs"); return; }
      if (num === Number(huidig)) return;
      saveRow(r.organisatie_id, { verkoopprijs: num }, `Klantprijs = ${fmtEUR(num)}`);
    }
  };

  const remove = async (orgId, naam) => {
    if (!window.confirm(`Klantcode/-prijs voor ${naam} verwijderen?`)) return;
    try {
      await api.delete(`/organisaties/${orgId}/prijzen/${productId}`);
      toast.success("Verwijderd"); load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const addNew = async () => {
    if (!picked) { toast.error("Kies eerst een organisatie"); return; }
    if (!newCode.trim() && !newPrijs) { toast.error("Vul een klantcode of prijs in"); return; }
    const body = {};
    if (newCode.trim()) body.klantcode = newCode.trim();
    if (newPrijs) {
      const num = parseFloat(String(newPrijs).replace(",", "."));
      if (!isNaN(num) && num >= 0) body.verkoopprijs = num;
    }
    await saveRow(picked.id, body, `Toegevoegd voor ${picked.naam}`);
    setPicked(null); setQ(""); setNewCode(""); setNewPrijs(""); setResults([]);
  };

  return (
    <div className="space-y-3" data-testid="product-klantcodes">
      <div className="text-xs text-muted-foreground">
        Klantspecifieke code per organisatie. Deze code vervangt onze SKU op de pakbon en factuur van die klant.
      </div>

      {/* Nieuwe koppeling */}
      <div className="rounded-lg border border-border p-3 space-y-2 relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek organisatie…" value={q}
            onChange={(e)=>{ setQ(e.target.value); setPicked(null); }}
            data-testid="klantcode-org-search" />
          {results.length > 0 && !picked && (
            <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden">
              {results.map(o => (
                <button key={o.id} type="button"
                  className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                  onClick={()=>{ setPicked(o); setQ(o.naam); setResults([]); }}
                  data-testid={`klantcode-org-opt-${o.id}`}>
                  <Building2 className="h-4 w-4 text-muted-foreground" /> {o.naam}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Klantcode" value={newCode} onChange={(e)=>setNewCode(e.target.value)}
            className="w-40 font-mono text-xs" data-testid="klantcode-new-code" />
          <Input placeholder="Prijs (optioneel)" inputMode="decimal" value={newPrijs} onChange={(e)=>setNewPrijs(e.target.value)}
            className="w-36" data-testid="klantcode-new-prijs" />
          <Button size="sm" onClick={addNew} data-testid="klantcode-add-btn"><Plus className="h-4 w-4 mr-1" />Toevoegen</Button>
        </div>
      </div>

      {/* Bestaande koppelingen */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left p-2">Organisatie</th>
              <th className="text-left p-2">Klantcode</th>
              <th className="text-right p-2">Klantprijs</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.organisatie_id} data-testid={`klantcode-row-${r.organisatie_id}`}>
                <td className="p-2 font-medium">{r.organisatie_naam}</td>
                <td className="p-2">
                  <Input defaultValue={r.klantcode || ""} placeholder="—"
                    onBlur={(e)=>onBlurField(r, "klantcode", e.target.value)}
                    className="w-32 h-8 font-mono text-xs"
                    data-testid={`klantcode-edit-${r.organisatie_id}`} />
                </td>
                <td className="p-2 text-right">
                  <Input defaultValue={r.verkoopprijs ?? ""} placeholder="—" inputMode="decimal"
                    onBlur={(e)=>onBlurField(r, "verkoopprijs", e.target.value)}
                    className="w-28 h-8 text-right ml-auto"
                    data-testid={`klantprijs-edit-${r.organisatie_id}`} />
                </td>
                <td className="p-2 text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                    onClick={()=>remove(r.organisatie_id, r.organisatie_naam)}
                    data-testid={`klantcode-del-${r.organisatie_id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">Nog geen klantspecifieke codes.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
